import { GridCellParams } from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import Chip from '@mui/material/Chip';
import { Fragment, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { BehaviorSubject } from 'rxjs';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { UpstackDataGrid } from '../UpstackDataGrid/UpstackDataGrid';
import { Icon } from '../../Atoms/Icon';
import { Text } from '../../Atoms/Text';
import { ProvisionalAsset } from '../../../store/ProvisionalAsset';
import { addDrawer } from '../../../store/Drawer';
import { ComponentName, getMetaData } from '../DetailsDrawer/detailsDrawersHelpers';
import { GridCustomColDef } from '../../types';
import { chipStyles, formatPercent, formatPrice, getDate, getFormattedDate } from '../../../utils/helpers';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { isData, isObserver } from '../../../store/User';
import { ApiAdvancedFilter, PaginatedResponse } from '../../../store/GeneralStore';
import { RecordLink } from '../../Atoms/RecordLink';

const handleProvisionalAssetClick = (row: ProvisionalAsset) => {
  addDrawer({
    id: v4(),
    data: row,
    ...getMetaData({ name: ComponentName.PROVISIONAL_ASSET, id: row.id }),
    replace: false
  });
};

export const allCols: GridCustomColDef[] = [
  {
    headerName: 'Number',
    field: 'assetNumber',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <button
        className="flex items-center"
        onClick={() => handleProvisionalAssetClick(params.row)}>
        <Icon
          className="p-3 mr-1"
          type="provisionalAssetMed"
        />
        <Text
          size="sm14"
          color="indigo">
          {params?.row.assetNumber as string}
        </Text>
      </button>
    )
  },
  {
    headerName: 'Comm. Status',
    field: 'commissionStatus',
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    },
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false
  },
  {
    headerName: 'Act. First Run',
    field: 'actualFirstCommissionRun',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    }
  },
  {
    headerName: 'Supplier',
    field: 'supplierName',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    renderCell: (params: GridCellParams) => params.value || params.row.supplier
  },
  {
    headerName: 'Customer',
    field: 'accountName',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    renderCell: (params: GridCellParams) => (
      <RecordLink to={`/customer/${params.row.accountId}`}>
        <div className="flex items-center text-small">{(params.value || params.row.accountId) as string}</div>
      </RecordLink>
    )
  },
  {
    headerName: 'Comm. MRC',
    field: 'commissionableMrc',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    headerName: 'Recurring %',
    field: 'recurringCommission',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    )
  },
  {
    headerName: 'Gross MRR',
    field: 'expectedGrossMrr',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  }
];

export interface ProvisionalAssetMiniTableParams {
  id: string;
  fetchFunction: (
    id: string,
    drawer?: boolean
  ) => (filters?: ApiAdvancedFilter) => Promise<PaginatedResponse<ProvisionalAsset> | undefined | void>;
  dataSubject: () => BehaviorSubject<PaginatedResponse<ProvisionalAsset>>;
  loadingSubject: () => BehaviorSubject<boolean>;
  removeFunction?: (id: string) => Promise<void>;
  showRemove?: boolean;
  type?: string;
  drawer?: boolean;
}

export const ProvisionalAssetMiniTable = ({
  id,
  fetchFunction,
  dataSubject,
  loadingSubject,
  removeFunction,
  showRemove,
  type,
  drawer = false
}: ProvisionalAssetMiniTableParams) => {
  const [provisionalAssets, setProvisionalAssets] = useState<PaginatedResponse<ProvisionalAsset>>(
    {} as PaginatedResponse<ProvisionalAsset>
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const { setSnack } = useSnackbar();

  const handleRemove = async (row: ProvisionalAsset) => {
    const assetId = type === 'Reference' ? row.id : row.linkId;
    if (removeFunction && showRemove && assetId) {
      await removeFunction(assetId);
      setSnack({ message: `${type || 'Link'} removed`, type: 'success', open: true });
      fetchFunction(id, drawer)();
    }
  };

  useEffect(() => {
    const provisionalAssetSub = dataSubject().subscribe((r) => {
      if (!tableLoading) setProvisionalAssets(r);
      setLoading(false);
    });
    const provisionalAssetLoadingSub = loadingSubject().subscribe((l) => setTableLoading(l));

    return () => {
      if (provisionalAssetSub) provisionalAssetSub.unsubscribe();
      if (provisionalAssetLoadingSub) provisionalAssetLoadingSub.unsubscribe();
    };
  }, [tableLoading]);

  useEffect(() => {
    if (id) fetchFunction(id, drawer)();
  }, [id, drawer]);

  const drawerHiddenFields = ['supplierName', 'accountName', 'recurringCommission'];
  const tableCols: GridCustomColDef[] = [
    ...(drawer ? allCols.filter((col) => !drawerHiddenFields.includes(col.field)) : allCols)
  ];

  if (!isObserver() && !isData())
    tableCols.push({
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      type: 'singleSelect',
      valueOptions: [],
      sortable: false,
      renderCell: (params) => (
        <Fragment>
          {showRemove && (
            <button
              className="-mt-[0.125rem] align-top"
              onClick={() => handleRemove(params.row)}>
              <DeleteIcon color="buttonGrey" />
            </button>
          )}
        </Fragment>
      )
    });

  const sxStyle = {
    ...DEFAULT_MINI_TABLE_SX_STYLES,
    minHeight: 'inherit',
    height: 'inherit'
  };

  return (
    <div
      className="w-full"
      data-testid="provisional-asset-mini-table-container">
      {loading && <ComponentSpinner label="Loading Provisional Asset Data" />}
      {!loading && (
        <UpstackDataGrid
          rows={provisionalAssets.data || []}
          sxStyles={sxStyle}
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
            sorting: { sortModel: [{ field: 'assetNumber', sort: 'desc' }] }
          }}
          currentPage={provisionalAssets.page}
          rowCount={provisionalAssets.totalCount || 0}
          columns={tableCols}
          dataCy="provisional-asset-mini-data-grid-pagination"
          fetchFunction={fetchFunction(id, drawer)}
          title="provisionalAsset"
          showSearch={false}
          page="provisionalAssetGrid_MiniTable"
          apiFilter
          gridSettings={{ muiConfig: {}, disabledFields: [] }}
          pageSizeOptions={[50]}
          showToolbar={false}
          withoutSave
          localLoading={tableLoading}
          pagination={provisionalAssets.totalPages > 1}
        />
      )}
    </div>
  );
};
